import React, { useEffect, useMemo, useState } from 'react'
import DataTable from '../../../../../ui/DataTable/DataTable'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import { withRouter } from 'react-router-dom'
import { db } from '../../../../../config/firebase'
import { find, isEmpty, orderBy } from 'lodash'
import './ProductsTable.sass'
import getCollection from '../../../../../utils/db/getCollection'
import getDoc from '../../../../../utils/db/getDoc'
import promiseAllValues from '../../../../../utils/promiseAllValues'
import { CATALOG_TAXONOMY_COLLECTION } from '../../CatalogTaxonomy/catalogTaxonomyConstants'
import {
  buildCatalogTaxonomyNodesById,
  formatProductTaxonomySummary,
} from '../../../productSchema/formatProductTaxonomySummary'
import {
  filterProductsByTableFilters,
  isProductArchived,
  PRODUCTS_ACTIVE_FILTER,
  PRODUCTS_TAXONOMY_FILTER,
} from './filterProductsByTableFilters'
import ProductsTableFilters from './ProductsTableFilters'
import ProductsBulkTaxonomyActions from './ProductsBulkTaxonomyActions'
import { getProductsTableColumns, isOutletTableVariant, PRODUCTS_TABLE_VARIANTS } from './getProductsTableColumns'

const OUTLET_SHOP_ID = '854'

function ProductsTable({
  brandId,
  productsPath = 'products',
  productBasePath = '/products/list',
  variant = PRODUCTS_TABLE_VARIANTS.catalog,
  ...router
}) {
  const [products, setProducts] = useState([])
  const [taxonomyNodes, setTaxonomyNodes] = useState([])
  const [taxonomyFilter, setTaxonomyFilter] = useState(PRODUCTS_TAXONOMY_FILTER.all)
  const [activeFilter, setActiveFilter] = useState(PRODUCTS_ACTIVE_FILTER.all)
  const [selectionModel, setSelectionModel] = useState([])
  const isOutlet = isOutletTableVariant(variant)

  const columns = useMemo(() => getProductsTableColumns(variant), [variant])
  const defaultSortField = 'taxonomyMainLine'
  const nodesById = useMemo(() => buildCatalogTaxonomyNodesById(taxonomyNodes), [taxonomyNodes])

  const activeProducts = useMemo(() => {
    if (isOutlet) return products
    return products.filter((product) => !isProductArchived(product))
  }, [products, isOutlet])

  const archivedProducts = useMemo(() => {
    if (isOutlet) return []
    return products.filter((product) => isProductArchived(product))
  }, [products, isOutlet])

  const filteredActiveProducts = useMemo(() => {
    if (isOutlet) return products
    return filterProductsByTableFilters(activeProducts, {
      taxonomyFilter,
      activeFilter,
    })
  }, [products, activeProducts, isOutlet, taxonomyFilter, activeFilter])

  const selectedProducts = useMemo(() => {
    if (!selectionModel.length) return []
    const selected = new Set(selectionModel.map(String))
    return filteredActiveProducts.filter((product) => selected.has(String(product.id)))
  }, [filteredActiveProducts, selectionModel])

  useEffect(() => {
    setSelectionModel([])
  }, [brandId, taxonomyFilter, activeFilter])

  useEffect(() => {
    let cancelled = false
    let unsubOutlet = () => null
    let unsubPremium = () => null

    if (isOutlet) {
      let brand = null
      let outletShop = null
      let nextNodesById = {}
      let premiumById = {}
      let outletSnap = null

      const applyOutletRows = () => {
        if (cancelled || !brand || !outletSnap) return

        const data = []
        outletSnap.forEach((doc) => {
          const productData = doc.data()
          const premiumProduct = premiumById[doc.id] || null
          const taxonomySummary = formatProductTaxonomySummary(premiumProduct || {}, nextNodesById)
          const remains = [
            {
              id: outletShop ? outletShop.id : '854',
              name: outletShop ? outletShop.name : 'Outlet',
              quantity: getOutletProductQuantity({
                remains: outletShop ? outletShop.remains : [],
                productId: doc.id,
              }),
            },
          ]

          data.push({
            id: doc.id,
            ...productData,
            brand: brand.name,
            avatar: find(productData.photos, ['isAvatar', true]) || null,
            remains,
            taxonomySummary: taxonomySummary.sortKey || taxonomySummary.text,
            taxonomyMainLine: taxonomySummary.mainLine,
            taxonomyExtrasPreviewLines: taxonomySummary.extrasPreviewLines,
            taxonomyExtrasTooltip: taxonomySummary.extrasLines.join('\n'),
            taxonomySummaryLines: taxonomySummary.lines,
            taxonomyTooltipLines: taxonomySummary.tooltipLines,
          })
        })

        setProducts(orderBy(data, ['name'], ['asc']))
      }

      promiseAllValues({
        brand: getDoc({ path: 'brands', docId: brandId, docIdName: 'id' }),
        taxonomyNodes: getCollection({
          path: CATALOG_TAXONOMY_COLLECTION,
          docIdName: 'id',
        }),
        outletShop: getDoc({ path: 'shops', docId: OUTLET_SHOP_ID, docIdName: 'id' }),
      }).then((res) => {
        if (cancelled) return

        brand = res.brand
        outletShop = res.outletShop || null
        const nextNodes = res.taxonomyNodes || []
        nextNodesById = buildCatalogTaxonomyNodesById(nextNodes)
        setTaxonomyNodes(nextNodes)

        unsubPremium = db
          .collection('products')
          .where('brandId', '==', brandId)
          .onSnapshot((snap) => {
            const nextPremiumById = {}
            snap.forEach((doc) => {
              nextPremiumById[doc.id] = { id: doc.id, ...doc.data() }
            })
            premiumById = nextPremiumById
            applyOutletRows()
          })

        unsubOutlet = db
          .collection(productsPath)
          .where('brandId', '==', brandId)
          .onSnapshot((snap) => {
            outletSnap = snap
            applyOutletRows()
          })
      })

      return () => {
        cancelled = true
        unsubOutlet()
        unsubPremium()
      }
    }

    let dbProductsListener = () => null

    promiseAllValues({
      brand: getDoc({ path: 'brands', docId: brandId, docIdName: 'id' }),
      taxonomyNodes: getCollection({
        path: CATALOG_TAXONOMY_COLLECTION,
        docIdName: 'id',
      }),
      remains: getCollection({ path: 'shops', docIdName: 'id' }),
    }).then((res) => {
      if (cancelled) return

      const { brand } = res
      const nextNodes = res.taxonomyNodes || []
      const nextNodesById = buildCatalogTaxonomyNodesById(nextNodes)
      setTaxonomyNodes(nextNodes)

      const shopRemains = (res.remains || []).filter((shop) => String(shop.id) !== OUTLET_SHOP_ID)

      dbProductsListener = db
        .collection(productsPath)
        .where('brandId', '==', brandId)
        .onSnapshot((snap) => {
          const data = []
          snap.forEach((doc) => {
            const productData = doc.data()
            const remains = orderBy(shopRemains, ['pos'], ['asc']).map((shop) => {
              const productInShop = find(shop.remains, { productId: doc.id })
              return {
                id: shop.id,
                name: shop.name,
                quantity: !isEmpty(productInShop) ? productInShop.quantity : 0,
              }
            })

            const taxonomySummary = formatProductTaxonomySummary(productData, nextNodesById)
            data.push({
              id: doc.id,
              ...productData,
              brand: brand.name,
              avatar: find(productData.photos, ['isAvatar', true]) || null,
              remains,
              taxonomySummary: taxonomySummary.sortKey || taxonomySummary.text,
              taxonomyMainLine: taxonomySummary.mainLine,
              taxonomyExtrasPreviewLines: taxonomySummary.extrasPreviewLines,
              taxonomyExtrasTooltip: taxonomySummary.extrasLines.join('\n'),
              taxonomySummaryLines: taxonomySummary.lines,
              taxonomyTooltipLines: taxonomySummary.tooltipLines,
            })
          })
          setProducts(orderBy(data, ['name'], ['asc']))
        })
    })

    return () => {
      cancelled = true
      dbProductsListener()
    }
  }, [brandId, productsPath, isOutlet])

  const openProduct = (productId) => {
    router.history.push(`${productBasePath}/${brandId}/${productId}`)
  }

  if (isOutlet) {
    return (
      <ModulePanel title="Товари">
        <DataTable
          initialState={{
            sorting: {
              sortModel: [{ field: defaultSortField, sort: 'asc' }],
            },
          }}
          table={{
            rows: products,
            columns,
            autoHeight: true,
            onRowDoubleClick: (params) => openProduct(params.row.id),
          }}
        />
      </ModulePanel>
    )
  }

  return (
    <div className="ProductsTableStack">
      <ModulePanel title={`Товари (${filteredActiveProducts.length})`}>
        <ProductsTableFilters
          taxonomyFilter={taxonomyFilter}
          activeFilter={activeFilter}
          onChange={(next) => {
            if (next.taxonomyFilter != null) setTaxonomyFilter(next.taxonomyFilter)
            if (next.activeFilter != null) setActiveFilter(next.activeFilter)
          }}
        />
        <div className="ProductsTableDivider" aria-hidden="true" />
        <ProductsBulkTaxonomyActions
          selectedProducts={selectedProducts}
          nodes={taxonomyNodes}
          nodesById={nodesById}
          onDone={() => setSelectionModel([])}
          onClearSelection={() => setSelectionModel([])}
        />
        <DataTable
          initialState={{
            sorting: {
              sortModel: [{ field: defaultSortField, sort: 'asc' }],
            },
          }}
          table={{
            rows: filteredActiveProducts,
            columns,
            autoHeight: true,
            checkboxSelection: true,
            disableRowSelectionOnClick: true,
            rowSelectionModel: selectionModel,
            onRowSelectionModelChange: (next) => setSelectionModel(next || []),
            onRowDoubleClick: (params) => openProduct(params.row.id),
          }}
        />
      </ModulePanel>

      <ModulePanel title={`Архівні товари (${archivedProducts.length})`}>
        {isEmpty(archivedProducts) ? (
          <p className="ProductsTableEmpty">Немає архівних товарів</p>
        ) : (
          <DataTable
            initialState={{
              sorting: {
                sortModel: [{ field: defaultSortField, sort: 'asc' }],
              },
            }}
            table={{
              rows: archivedProducts,
              columns,
              autoHeight: true,
              onRowDoubleClick: (params) => openProduct(params.row.id),
            }}
          />
        )}
      </ModulePanel>
    </div>
  )
}

const getOutletProductQuantity = ({ remains = [], productId }) => {
  const productInOutlet = find(remains, { productId })
  return !isEmpty(productInOutlet) ? productInOutlet.quantity : 0
}

export default withRouter(ProductsTable)
